<script setup lang="ts">
import { ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import capitalize from 'lodash/capitalize';
import { getTextVariant } from '@/utils/stringUtils';
import { formatDate } from '@/utils/dateUtils';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useAirManagementStore } from '@/store/AirManagementStore';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import { useWorksheetComposable } from './useWorksheetComposable';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import ViewFlightSeat from '@/components/modal/worksheet/ViewFlightSeat.vue';

interface Props {
  tour: any;
  isView?: boolean;
  toursInfo?: any;
  tabIndex?: number;
}
const props = defineProps<Props>();

const emits = defineEmits<{ handleTour: [value: any] }>();

const { t } = useI18n({ useScope: 'global' });
const lang = getCurrentLocale();
const langCapitalize = capitalize(lang);
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;

const tourRef = ref(props.tour);
const toast = useToast();
const dialog = useDialog();
const adminQuoteStore = useAdminQuoteStore();
const airManagementStore = useAirManagementStore();
const { getCabinName, formatFlightData, isCheckValidateDate, LIST_CABIN, validateFlightNumber, airportList, onFilterAirport, initFlightData } = useWorksheetComposable();

const fieldColumns = ref([
  { field: 'groupBy', header: '' },
  { field: 'type', header: t('worksheet.type') },
  { field: 'operated', header: t('worksheet.airline') },
  { field: 'flightNumber', header: t('worksheet.flight_number') },
  { field: 'gate', header: t('worksheet.from') },
  { field: 'dest', header: t('worksheet.to') },
  { field: 'depDate', header: t('worksheet.dep_date') },
  { field: 'depTime', header: t('worksheet.dep_time') },
  { field: 'arrDate', header: t('worksheet.arrival_date') },
  { field: 'arrTime', header: t('worksheet.arrival_time') },
  { field: 'via', header: t('worksheet.via') },
  { field: 'fClass', header: t('common.class') },
  { field: 'cabin', header: t('common.cabin') },
  { field: 'rowEditor', header: '' }
]);

const filterGateway = ref([]);
const filterDestination = ref([]);
const editingFlightRows = ref<any>([]);
const airlineList = ref([]);
const isLoadingSubmit = ref(false);
const availability = authorizations.worksheet_tour_flight_header.availability_view;
const flight_edit = authorizations.worksheet_tour_flight_header.flight_edit;

const formatFlightList = (flights: any) => {
  return formatFlightData(flights);
};

const handleColumn = () => {
  return props.isView ? fieldColumns.value.filter((column: any) => column.field !== 'groupBy' && column.field !== 'rowEditor') : fieldColumns;
};

const columns = ref(handleColumn());

const listFlight = ref(formatFlightList(props.tour.flights));

const getTime = (date: any) => {
  return date ? DateTime.fromISO(new Date(date).toISOString()).toLocal().toFormat('T') : '';
};

const getListAirline = async () => {
  const response = await adminQuoteStore.getAirlines();
  if (response) {
    airlineList.value = response;
  }
};
getListAirline();

const onRowEditSaveFlight = (event: any, pFlight: any) => {
  const { index, newData } = event;
  const newGateFlight = newData?.gate;
  const newDestFlight = newData?.dest;

  let depTime = newData?.depTime?.toString();
  if (depTime?.length > 5) {
    depTime = getTime(depTime);
  }

  let arrTime = newData?.arrTime?.toString();
  if (arrTime?.length > 5) {
    arrTime = getTime(arrTime);
  }

  if (typeof newGateFlight === 'object') {
    newData.gateCode = newGateFlight.airportCode;
    newData.gate = `${newGateFlight.airportNameLabel} (${newGateFlight.airportCode})`;
    newData.gateFr = `${newGateFlight.airportNameLabelFr} (${newGateFlight.airportCode})`;
  }
  if (typeof newDestFlight === 'object') {
    newData.destCode = newDestFlight.airportCode;
    newData.dest = `${newDestFlight.airportNameLabel} (${newDestFlight.airportCode})`;
    newData.destFr = `${newDestFlight.airportNameLabelFr} (${newDestFlight.airportCode})`;
  }

  if (isCheckValidateDate(newData, pFlight)) {
    newData.isNew = false;

    pFlight[index] = { ...newData, depTime, arrTime };
    pFlight[index].airline = airlineList.value?.find((air: any) => air.code === pFlight[index].operated)?.name;
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.validate_date') });
  }
};

const addNewFlight = (type: string) => {
  const groupFlights = listFlight.value.filter((flight: any) => flight.type === type);
  const numberOfFlight = groupFlights?.length;
  if (numberOfFlight < 4) {
    let lastIndex = -1;
    listFlight.value?.forEach((flight: any, index: number) => {
      if (flight.type === type) {
        lastIndex = index;
      }
    });
    const initFlightKey = `flight${Date.now()}${listFlight.value.length}`;
    listFlight.value.splice(lastIndex + 1, 0, initFlightData(type, initFlightKey));
    editingFlightRows.value = listFlight.value.filter((flight: any) => editingFlightRows.value.find((row: any) => row.key === flight.key) || flight.key === initFlightKey);
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('worksheet.maximum_flight_notification') });
  }
};

const deleteFlight = (flight: any) => {
  listFlight.value = listFlight.value.filter((flightItem: any) => flightItem.key !== flight.key);
};

const validateFlightData = (flightsData: any) => {
  return flightsData.every((flight: any) => Object.values(flight).every((flightKey: any) => flightKey !== null && flightKey !== undefined && flightKey !== ''));
};

const handleOpenViewFlightSeat = () => {
  const flightTemp: any = [];
  listFlight.value?.forEach((flight: any) => {
    const flightOption = {
      type: flight.type,
      gateCode: flight.gateCode,
      destCode: flight.destCode,
      depDate: formatDate(flight.depDate, undefined, false),
      retDate: formatDate(flight.arrDate, undefined, false),
      direct: !!flight.direct,
      airlineCode: flight.operated,
      number: flight.flightNumber
    };
    flightTemp.push(flightOption);
  });

  const payload = [
    {
      gateCode: props.toursInfo.gateCode,
      destCode: props.tour?.ttsId?.slice(0, 3) || props.toursInfo.airportCode,
      depDate: formatDate(props.tour.depDate, undefined, false),
      retDate: formatDate(props.tour.retDate, undefined, false),
      direct: !!props.toursInfo.direct,
      flightRequest: flightTemp,
      option: `${props.tabIndex ? props.tabIndex + 1 : 0}`
    }
  ];

  isLoadingSubmit.value = true;
  const validateDataFlight = validateFlightData(flightTemp);
  if (validateDataFlight) {
    airManagementStore
      .getFlightSeat(payload)
      .then((res) => {
        dialog.open(ViewFlightSeat, {
          data: {
            flightInfo: listFlight.value,
            flightSeatInfo: res
          },
          props: {
            header: t('worksheet.flight_details'),
            maximizable: true,
            style: {
              width: '95vw'
            },
            breakpoints: {
              '960px': '75vw',
              '640px': '90vw'
            },
            modal: true
          }
        });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
      })
      .finally(() => {
        isLoadingSubmit.value = false;
      });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.please_fill_field') });
    isLoadingSubmit.value = false;
  }
};

const dataTypeString = (field: string) => {
  if (['type', 'operated', 'flightNumber', 'gate', 'dest', 'cabin'].includes(field)) {
    return 'string';
  }
  if (['depTime', 'arrTime', 'fClass'].includes(field)) {
    return 'date';
  }
  return undefined;
};

const setClassForColumn = (field: string) => {
  if (['type', 'flightNumber', 'fClass'].includes(field)) {
    return 'min-w-5rem';
  }
  if (['operated', 'depTime', 'arrTime'].includes(field)) {
    return 'min-w-10rem';
  }
  if (['gate', 'dest', 'depDate', 'arrDate'].includes(field)) {
    return 'min-w-10rem';
  }
  if (['via'].includes(field)) {
    return 'w-5rem min-w-5rem';
  }
  if (['cabin'].includes(field)) {
    return 'word-break w-10rem max-w-10rem';
  }
  if (['rowEditor'].includes(field)) {
    return 'text-center';
  }
};

const searchPackageGateway = (event: any) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterGateway.value = [...airportList.value.slice(0, 100)];
  } else {
    filterGateway.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

const searchPackageDestination = (event: any) => {
  if (!event.query.trim().length || event.originalEvent.type !== 'input') {
    filterDestination.value = [...airportList.value.slice(0, 100)];
  } else {
    filterDestination.value = airportList.value.filter((air) => {
      return onFilterAirport(air, event);
    });
  }
};

watch(
  listFlight.value,
  (newVal: any) => {
    if (newVal) {
      tourRef.value.flights = newVal;
      emits('handleTour', tourRef.value);
    }
  },
  { deep: true }
);
</script>
<template>
  <div>
    <div class="pricing flight-detail">
      <div class="lead-flight flex align-items-center justify-content-between">
        {{ $t('common.flight_details') }}
        <Button v-if="!isView && availability" size="small" severity="danger" class="view-availability-button" :label="$t('common.buttons.view_availability')" :loading="isLoadingSubmit" @click="handleOpenViewFlightSeat()" />
      </div>

      <div class="details">
        <DataTable v-model:editingRows="editingFlightRows" :value="listFlight" :rowGroupMode="isView ? '' : 'subheader'" groupRowsBy="groupBy" class="w-full" showGridlines editMode="row" @row-edit-save="onRowEditSaveFlight($event, listFlight)">
          <template #groupheader="slotProps">
            <div class="flex align-items-center justify-content-end gap-2">
              <div class="add-flight-button" @click="addNewFlight(slotProps.data.type)">+ {{ $t(`worksheet.add_${slotProps.data.type.toLowerCase()}`) }}</div>
            </div>
          </template>
          <TableColumn v-for="column of columns" :key="column.field" :field="column.field" :dataType="dataTypeString(column.field)" :class="setClassForColumn(column.field)" :rowEditor="column.field === 'rowEditor'">
            <template #header>
              <span :class="{ 'flex justify-content-center gap-3 align-content-center w-full': true }">
                {{ column.header }}
              </span>
            </template>
            <template #body="{ data, field, editorInitCallback }">
              <span v-if="column.field === 'type'">
                {{ $t(`worksheet.${data[field].toLowerCase()}`) }}
              </span>
              <span v-else-if="column.field === 'operated'">
                {{ data.airline }}
              </span>
              <span v-else-if="column.field === 'gate' || column.field === 'dest'">
                {{ getTextVariant(lang, data[field], data[`${field}${langCapitalize}`]) }}
              </span>
              <span v-else-if="column.field === 'depDate' || column.field === 'arrDate'">
                {{ formatDate(data[field], 'dd-LLL-yyyy', false, lang) }}
              </span>
              <span v-else-if="column.field === 'cabin'">
                {{ getCabinName(data[field]) }}
              </span>
              <div v-else-if="column.field === 'rowEditor' && flight_edit" class="flex flex-column">
                <i class="pi pi-pencil table-action-button" @click="editorInitCallback()"></i>
                <i v-if="data.canDelete" class="pi pi-times-circle table-action-button text-primary" @click="deleteFlight(data)"></i>
              </div>
              <span v-else>
                {{ data[field] }}
              </span>
            </template>
            <template #editor="{ data, field, editorSaveCallback, editorCancelCallback }">
              <span v-if="column.field === 'type'">
                {{ $t(`worksheet.${data[field].toLowerCase()}`) }}
              </span>
              <span v-if="column.field === 'operated'">
                <Select v-model="data[field]" :options="airlineList" optionLabel="name" optionValue="code" :placeholder="$t('worksheet.select_type')" class="w-full" />
              </span>
              <span v-if="column.field === 'flightNumber'">
                <InputText class="flight-number-class" v-model="data[field]" :maxlength="5" @input="validateFlightNumber(data, field)" />
              </span>
              <span v-if="column.field === 'gate'">
                <AutoComplete v-model="data[field]" :suggestions="filterGateway" @complete="searchPackageGateway($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                  <template #option="slotProps">
                    <div class="flex align-options-center">
                      <div>
                        <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                        <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                      </div>
                    </div>
                  </template>
                </AutoComplete>
              </span>
              <span v-if="column.field === 'dest'" class="dest-class">
                <AutoComplete v-model="data[field]" :suggestions="filterDestination" @complete="searchPackageDestination($event)" completeOnFocus optionLabel="selectedLabel" class="w-full" forceSelection>
                  <template #option="slotProps">
                    <div class="flex align-options-center">
                      <div>
                        <div>{{ slotProps.option.airportName }} ({{ slotProps.option.airportCode }})</div>
                        <div class="text-sm">{{ slotProps.option.cityName }}, {{ slotProps.option.province || slotProps.option.region }}, {{ slotProps.option.countryName }}</div>
                      </div>
                    </div>
                  </template>
                </AutoComplete>
              </span>
              <span v-if="column.field === 'depDate' || column.field === 'arrDate'">
                <DatePicker id="calendar-24h" :class="`${column.field}-class`" v-model="data[field]" dateFormat="dd-M-yy">
                  <template #footer>
                    <p class="text-center">
                      <small>{{ $t('common.press_space') }}</small>
                    </p>
                  </template>
                </DatePicker>
              </span>
              <span v-if="column.field === 'depTime' || column.field === 'arrTime'" :class="`${column.field}-class`">
                <DatePicker :modelValue="data[field] ? (data[field].toString().length > 6 ? new Date(data[field]) : new Date(DateTime.fromFormat(data[field], 'T'))) : ''" id="calendar-24h" v-model="data[field]" showTime hourFormat="24" timeOnly>
                  <template #footer>
                    <p class="text-center">
                      <small>{{ $t('common.press_space') }}</small>
                    </p>
                  </template>
                </DatePicker>
              </span>
              <span v-if="column.field === 'fClass'">
                <InputText class="f-class" v-model="data[field]" :maxlength="5" />
              </span>
              <span v-if="column.field === 'cabin'" class="cabin-class">
                <Select v-model="data[field]" :options="LIST_CABIN" optionLabel="name" optionValue="data" :placeholder="$t('worksheet.select_type')" class="w-full" />
              </span>
              <span v-if="column.field === 'via'">
                <InputText v-model="data[field]" :maxlength="3" class="via-class" />
              </span>
              <div class="flex flex-column" v-if="column.field === 'rowEditor'">
                <i class="pi pi-check table-action-button" @click="editorSaveCallback()"></i>
                <i class="pi pi-times table-action-button" @click="editorCancelCallback()"></i>
              </div>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>
  </div>
</template>
